import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  AreaChart,
  Area,
  ComposedChart,
  LabelList,
} from "recharts";
import { Building, MapPin, Users, Grid, Shield, Car } from "lucide-react";
import { apiCall } from "../Apis/apiCall";
import Breadcrumb from "../components/Breadcrumb";
import CustomSelect from "../components/common/CustomSelect";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Import images (unchanged)
import carImage from "../images/car-parking.png";
import usersImage from "../images/driver-license.png";
import buildingImage from "../images/car.png";
import locationImage from "../images/car 2.png";

// // Mock data (unchanged)
// const mockAdminData = {
//   data: {
//     total_branches: 20,
//     total_locations: 50,
//     total_staff: 100,
//     total_slots: 5000,
//     total_roles: 10,
//     isExpired: false,
//   },
//   initial_data: {
//     vehicles_parked: 120,
//     active_sessions: 80,
//     current_occupancy: 150,
//     revenue_generated: [{ currency_code: "USD", total: 25000 }],
//   },
//   occupancy_overview: {
//     by_location: [
//       {
//         location_name: "Downtown Lot",
//         created_slots: 100,
//         occupied_slots: 80,
//         open_slots: 20,
//         occupancy_percent: 80,
//       },
//       {
//         location_name: "Uptown Lot",
//         created_slots: 150,
//         occupied_slots: 90,
//         open_slots: 60,
//         occupancy_percent: 60,
//       },
//       {
//         location_name: "Central Plaza",
//         created_slots: 200,
//         occupied_slots: 50,
//         open_slots: 150,
//         occupancy_percent: 25,
//       },
//     ],
//     hourly_trend: Array.from({ length: 24 }, (_, i) => ({
//       time: new Date(2025, 8, 24, i).toISOString(),
//       vehicles_started: Math.floor(Math.random() * 10),
//     })),
//   },
//   staff_performance: [
//     { staff_id: 1, staff_name: "John Doe", total_jobs: 45 },
//     { staff_id: 2, staff_name: "Jane Smith", total_jobs: 30 },
//     { staff_id: 3, staff_name: "Mike Johnson", total_jobs: 25 },
//   ],
//   revenue_analytics: {
//     payment_types: {
//       total_revenue: 25000,
//       breakdown: [
//         { payment_type: "Credit Card", amount: 15000, percent: 60 },
//         { payment_type: "Cash", amount: 7500, percent: 30 },
//         { payment_type: "Mobile Wallet", amount: 2500, percent: 10 },
//       ],
//     },
//     daily_trend: [
//       { date: "2025-09-17", total: 4000 },
//       { date: "2025-09-18", total: 5000 },
//       { date: "2025-09-19", total: 3000 },
//       { date: "2025-09-20", total: 6000 },
//       { date: "2025-09-21", total: 7000 },
//     ],
//   },
// };

// Shimmer components (aligned with second dashboard)
const ShimmerCard = () => (
  <div className="card border-0 shadow-sm radius-12 h-100">
    <div className="card-body p-20">
      <div className="d-flex align-items-center gap-3">
        {/* Icon Placeholder */}
        <div className="placeholder-glow">
          <div className="placeholder w-60-px h-60-px rounded-circle bg-secondary-light"></div>
        </div>
        {/* Text Placeholders */}
        <div className="flex-grow-1">
          <div className="placeholder-glow mb-2">
            <span className="placeholder col-9 bg-secondary-light" style={{ height: "14px" }}></span>
          </div>
          <div className="placeholder-glow">
            <span className="placeholder col-6 bg-secondary-light" style={{ height: "24px" }}></span>
          </div>
          <div className="placeholder-glow mt-2">
            <span className="placeholder col-7 bg-secondary-light" style={{ height: "12px" }}></span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ShimmerChart = () => (
  <div className="card border-0 shadow-sm radius-12 h-100">
    <div className="card-body p-24">
      {/* Title Placeholder */}
      <div className="placeholder-glow mb-20">
        <span className="placeholder col-4 h-24-px rounded bg-secondary-light"></span>
      </div>
      {/* Chart Area Placeholder */}
      <div className="placeholder-glow">
        <div
          className="placeholder w-100 h-300-px rounded-12 bg-secondary-light"
          style={{ minHeight: "320px" }}
        ></div>
      </div>
    </div>
  </div>
);

const ShimmerTable = () => (
  <div className="card border-0 shadow-sm radius-12 h-100">
    <div className="card-body p-24">
      <div className="placeholder-glow mb-20">
        <span className="placeholder col-5 h-24-px rounded bg-secondary-light"></span>
      </div>
      <div className="table-responsive">
        <table className="table bordered-table sm-table mb-0">
          <thead>
            <tr>
              {[...Array(5)].map((_, i) => (
                <th key={i}>
                  <div className="placeholder-glow">
                    <span className="placeholder col-8 bg-secondary-light" style={{ height: "16px" }}></span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...Array(6)].map((_, i) => (
              <tr key={i}>
                {[...Array(5)].map((_, j) => (
                  <td key={j}>
                    <div className="placeholder-glow">
                      <span className="placeholder col-10 bg-secondary-light" style={{ height: "14px" }}></span>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeDashboard, setActiveDashboard] = useState("admin");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientsList, setClientsList] = useState([]);
  const [counts, setCounts] = useState(null);
  const [paymentSources, setPaymentSources] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [clientCoordinates, setClientCoordinates] = useState([]);

  const [staffLoginData, setStaffLoginData] = useState([]); // NEW LINE
  const [initialData, setInitialData] = useState(null);
  const [topActiveClients, setTopActiveClients] = useState([]);
  const [topActiveLocations, setTopActiveLocations] = useState([]); // YEH NEW LINE

  // const [staffData, setStaffData] = useState([]);
  const [occupancyData, setOccupancyData] = useState({
    lotData: [],
    hourlyData: [],
  });
  const [revenueData, setRevenueData] = useState({
    paymentTypes: { total_revenue: 0, breakdown: [] },
    dailyTrend: [],
  });
  const [loading, setLoading] = useState(true);
  const [monthlyBillingData, setMonthlyBillingData] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    navigateTo: "",
    showCancel: true,
  });
  const [timeFrame, setTimeFrame] = useState("today");
  const [activeOccupancyTab, setActiveOccupancyTab] = useState("byLot");
  const [activeRevenueTab, setActiveRevenueTab] = useState("paymentTypes");
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    isRangeSelected: false
  });

  const timeFrameOptions = [
    { label: "Today", value: "today" },
    { label: "Weekly", value: "weekly" },
    { label: "Monthly", value: "monthly" },
  ];

  const paymentColors = [
    "#4B0082", // Indigo for Card
    "#4682B4", // Steel Blue for Wallet
    "#3CB371", // Medium Sea Green for Cash
    "#FFA500", // Orange for Online_transfer
    "#E63C23", // Red for Upi
    "#FF6B4A", // Light Red (extra)
  ];

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: "var(--rounded-8)",
      borderColor: "var(--border-color)",
      background: "var(--neutral-50)",
      boxShadow: "none",
      padding: "4px",
      "&:hover": {
        borderColor: "var(--primary-600)",
      },
    }),
    menu: (provided) => ({
      ...provided,
      background: "var(--neutral-50)",
      borderRadius: "var(--rounded-8)",
      boxShadow: "var(--shadow-4)",
    }),
    option: (provided, state) => ({
      ...provided,
      background: state.isSelected ? "var(--primary-100)" : "var(--neutral-50)",
      color: state.isSelected
        ? "var(--primary-600)"
        : "var(--text-secondary-light)",
      "&:hover": {
        background: "var(--primary-100)",
        color: "var(--primary-600)",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "var(--text-secondary-light)",
    }),
  };

  // Fetch clients list (unchanged)
  const fetchClientsList = async () => {
    try {
      setClientsLoading(true);
      const response = await apiCall(
        "get",
        `/get-all-clients?search=&page=1&per_page=100&sort_by=client_id&sort_order=desc&want_pagination=0`
      );
      if (response && response.data && response.data.success) {
        setClientsList(response.data.data || []);
      } else {
        setModalConfig({
          title: "Error",
          message: "Failed to load clients list.",
          navigateTo: "",
          showCancel: false,
        });
        setShowModal(true);
      }
    } catch (error) {
      console.error("Error fetching clients list:", error);
      setModalConfig({
        title: "Error",
        message:
          "Error fetching clients list: " + (error.message || "Unknown error"),
        navigateTo: "",
        showCancel: false,
      });
      setShowModal(true);
    } finally {
      setClientsLoading(false);
    }
  };

  // Fetch dashboard data (unchanged)
  useEffect(() => {
    let mounted = true;
    const fetchAllData = async () => {
      if (activeDashboard === "admin") {
        setLoading(true);
        try {
          // Fetch dashboard counts
          const response = await apiCall("get", "/get-dashboard-counts-admin");
          if (mounted && response?.data?.success) {
            // Map API response to counts state
            setCounts({
              total_active_clients:
                response.data.data.total_active_clients || 0,
              total_vehicles_parked_today:
                response.data.data.total_vehicles_parked_today || 0,
              total_branches: response.data.data.total_branches || 0,
              total_locations: response.data.data.total_locations || 0,
              total_staff: response.data.data.total_staff || 0,
              total_slots: response.data.data.total_slots || 0,
              total_roles: response.data.data.total_roles || 0,
              isExpired: response.data.data.isExpired || false,
            });

            // Fetch Billing Stats (Payment Sources + Monthly Revenue)
            let paymentSources = [];
            let monthlyBillingData = [];
            try {
              const billingResponse = await apiCall(
                "get",
                "/get-dashboard-billing-stats"
              );
              if (billingResponse?.data?.success && billingResponse.data.data) {
                const data = billingResponse.data.data;

                // Payment Sources → Pie Chart
                const totalPayments = data.payment_sources.reduce(
                  (sum, item) => sum + (parseFloat(item.total_payments) || 0),
                  0
                );
                paymentSources = data.payment_sources.map((item, index) => ({
                  name: item.payment_method,
                  value: parseFloat(item.total_payments || 0),
                  percentage: totalPayments
                    ? Math.round(
                        (parseFloat(item.total_payments) / totalPayments) * 100
                      )
                    : 0,
                  fill: paymentColors[index % paymentColors.length],
                }));

                // Monthly Billing Summary → Bar Chart
                // Filter out null months aur sirf valid months lo
                monthlyBillingData = data.monthly_billing_summary
                  .filter((item) => item.month !== null) // null months skip
                  .map((item) => ({
                    name: item.month.split(" ")[0], // "Oct" from "Oct 2025"
                    dark: parseFloat(item.total_subscription_amount) || 0,
                    light: parseFloat(item.total_invoice_amount) || 0,
                  }));
              }
            } catch (billingError) {
              console.error("Error fetching billing stats:", billingError);
            }
            setPaymentSources(paymentSources);
            setMonthlyBillingData(monthlyBillingData);

            // Fetch Top Active Clients
            const today = new Date();
            // Fetch Top Active Clients with dynamic date range
            const startDate = new Date(dateRange.startDate).toISOString();
            const endDate = new Date(dateRange.endDate).toISOString();

            try {
              const topClientsResponse = await apiCall(
                "get",
                `/get-top-active-clients?start_date=${startDate}&end_date=${endDate}`
              );
              if (mounted && topClientsResponse?.data?.success) {
                setTopActiveClients(topClientsResponse.data.data || []);
              }
            } catch (clientError) {
              console.error("Error fetching top active clients:", clientError);
              setTopActiveClients([]);
            }

            try {
              const staffLoginResponse = await apiCall(
                "get",
                "/staff-login-summary-today"
              );
              if (mounted && staffLoginResponse?.data?.success) {
                setStaffLoginData(staffLoginResponse.data.data || []);
              }
            } catch (staffLoginError) {
              console.error(
                "Error fetching staff login summary:",
                staffLoginError
              );
              setStaffLoginData([]);
            }

            try {
              const coordinatesResponse = await apiCall(
                "get",
                "/get-client-coordinates"
              );
              if (mounted && coordinatesResponse?.data?.success) {
                setClientCoordinates(coordinatesResponse.data.data || []);
              }
            } catch (coordError) {
              console.error("Error fetching client coordinates:", coordError);
              setClientCoordinates([]);
            }

            try {
              const startDate = new Date(dateRange.startDate).toISOString();
              const endDate = new Date(dateRange.endDate).toISOString();
              const topLocationsResponse = await apiCall(
                "get",
                `/get-top-active-locations?start_date=${startDate}&end_date=${endDate}`
              );
              if (mounted && topLocationsResponse?.data?.success) {
                setTopActiveLocations(topLocationsResponse.data.data || []);
              }
            } catch (locationError) {
              console.error(
                "Error fetching top active locations:",
                locationError
              );
              setTopActiveLocations([]);
            }

            checkForZeroValues(response.data.data);
          } else {
            throw new Error("Invalid response from dashboard counts API");
          }
        } catch (error) {
          console.error("Error fetching admin dashboard data:", error);
          if (mounted) {
            setModalConfig({
              title: "Error",
              message:
                "Error fetching dashboard data: " +
                (error.message || "Server error"),
              navigateTo: "",
              showCancel: false,
            });
            setShowModal(true);

            // Reset to empty states
            setCounts({
              total_active_clients: 0,
              total_vehicles_parked_today: 0,
              total_branches: 0,
              total_locations: 0,
              total_staff: 0,
              total_slots: 0,
              total_roles: 0,
              isExpired: false,
            });
            setInitialData(null);
            setStaffData([]);
            setOccupancyData({ lotData: [], hourlyData: [] });
            setRevenueData({
              paymentTypes: { total_revenue: 0, breakdown: [] },
              dailyTrend: [],
            });
            setPaymentSources([]);
            setMonthlyBillingData([]);
            setTopActiveClients([]);
            setStaffLoginData([]);
            setClientCoordinates([]);
            setTopActiveLocations([]); // YEH NEW LINE ADD KARO
          }
        } finally {
          setLoading(false);
        }
        return;
      }

      // Client dashboard logic (unchanged)
      if (!selectedClient || !selectedClient.client_id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const getDateRange = () => {
          const today = new Date();
          if (timeFrame === "today") {
            const todayStr = today.toISOString().split("T")[0];
            return { from_date: todayStr, to_date: todayStr };
          } else if (timeFrame === "weekly") {
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            return {
              from_date: weekAgo.toISOString().split("T")[0],
              to_date: today.toISOString().split("T")[0],
            };
          } else {
            const monthAgo = new Date(
              today.getTime() - 30 * 24 * 60 * 60 * 1000
            );
            return {
              from_date: monthAgo.toISOString().split("T")[0],
              to_date: today.toISOString().split("T")[0],
            };
          }
        };

        const { from_date, to_date } = getDateRange();
        const occupancyMode =
          activeOccupancyTab === "byLot" ? "by_location" : "hourly_trend";
        const revenueMode =
          activeRevenueTab === "paymentTypes" ? "payment_types" : "daily_trend";
        const endpoint = `/dashboard-by-client-id/${selectedClient.client_id}?keys[]=initial_data&keys[]=staff_performance&keys[]=occupancy_overview&keys[]=revenue_analytics&from_date=${from_date}&to_date=${to_date}&occupancy_mode=${occupancyMode}&revenue_mode=${revenueMode}`;

        const response = await apiCall("get", endpoint);
        await new Promise((resolve) => setTimeout(resolve, 1500));

        if (mounted && response?.data?.success) {
          setCounts(response.data.data);
          setInitialData(response.data.initial_data);
          setStaffData(response.data.staff_performance || []);

          if (occupancyMode === "by_location") {
            const lotData =
              response.data.occupancy_overview.by_location?.map((item) => ({
                name: item.location_name,
                occupied: item.occupied_slots,
                total: item.created_slots,
                percentage: Math.round(item.occupancy_percent),
              })) || [];
            setOccupancyData((prev) => ({ ...prev, lotData }));
          } else {
            const totalSlots = response.data.data.slots_created || 0;
            const hourlyMap = {};
            response.data.occupancy_overview.hourly_trend?.forEach((item) => {
              const hour = new Date(item.time).getHours();
              if (!hourlyMap[hour]) {
                hourlyMap[hour] = { vehicles_started: 0, count: 0 };
              }
              hourlyMap[hour].vehicles_started += item.vehicles_started;
              hourlyMap[hour].count += 1;
            });
            const hourlyData = Array.from({ length: 24 }, (_, i) => {
              const hour = i;
              const data = hourlyMap[hour] || { vehicles_started: 0, count: 0 };
              const vehicles = data.vehicles_started;
              return {
                hour: `${hour}h`,
                value: vehicles,
                isPeak: vehicles >= 5,
              };
            });
            setOccupancyData((prev) => ({ ...prev, hourlyData }));
          }

          setRevenueData({
            paymentTypes: response.data.revenue_analytics?.payment_types || {
              total_revenue: 0,
              breakdown: [],
            },
            dailyTrend: response.data.revenue_analytics?.daily_trend || [],
          });

          checkForZeroValues(response.data.data);
        } else {
          throw new Error("Invalid response from dashboard API");
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        if (mounted) {
          setModalConfig({
            title: "Error",
            message:
              "Error fetching dashboard data: " +
              (error.message || "Server error"),
            navigateTo: "",
            showCancel: false,
          });
          setShowModal(true);

          // Reset to empty states
          setCounts(null);
          setInitialData(null);
          setStaffData([]);
          setOccupancyData({ lotData: [], hourlyData: [] });
          setRevenueData({
            paymentTypes: { total_revenue: 0, breakdown: [] },
            dailyTrend: [],
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
    return () => {
      mounted = false;
    };
  }, [
    activeDashboard,
    selectedClient,
    timeFrame,
    activeOccupancyTab,
    activeRevenueTab,
     dateRange.isRangeSelected,
  ]);

  // Fetch clients list on component mount (unchanged)
  useEffect(() => {
    if (activeDashboard === "client") {
      fetchClientsList();
    }
  }, [activeDashboard]);

  const checkForZeroValues = (data) => {
    if (data.total_roles === 0 && data.isExpired === false) {
      setModalConfig({
        title: "No Roles",
        message:
          "You currently have no Roles. Would you like to create one now?",
        navigateTo: "/staff-role-create",
        showCancel: true,
      });
      setShowModal(true);
    } else if (data.total_staff === 0 && data.isExpired === false) {
      setModalConfig({
        title: "No Staff Members",
        message:
          "You currently have no staff members. Would you like to create one now?",
        navigateTo: "/staff-create",
        showCancel: true,
      });
      setShowModal(true);
    } else if (data.total_branches === 0 && data.isExpired === false) {
      setModalConfig({
        title: "No Branches",
        message:
          "You currently have no branches. Would you like to create one now?",
        navigateTo: "/branch-create",
        showCancel: true,
      });
      setShowModal(true);
    } else if (data.total_locations === 0 && data.isExpired === false) {
      setModalConfig({
        title: "No Locations",
        message:
          "You currently have no locations. Would you like to create one now?",
        navigateTo: "/branch-location-create",
        showCancel: true,
      });
      setShowModal(true);
    }
  };

  const handleModalConfirm = () => {
    setShowModal(false);
    navigate(modalConfig.navigateTo);
  };

  const handleModalCancel = () => {
    setShowModal(false);
  };

  const processedPaymentData = Array.isArray(revenueData.paymentTypes.breakdown)
    ? revenueData.paymentTypes.breakdown.map((item, index) => ({
        name: item.payment_method || "Unknown",
        value: parseFloat(item.amount || 0),
        percentage: parseFloat(item.percent || 0),
        fill: paymentColors[index % paymentColors.length],
      }))
    : [];

  const processedDailyData = Array.isArray(revenueData.dailyTrend)
    ? revenueData.dailyTrend.map((item) => ({
        name: item.date
          ? new Date(item.date).toLocaleDateString("en-US", {
              weekday: "short",
            })
          : "N/A",
        value: parseFloat(item.total || 0),
      }))
    : [];

  // World Wide Client Map Component
const WorldWideClientMap = ({ clientCoordinates }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapInstanceRef.current && mapRef.current && clientCoordinates.length > 0) {
      // Initialize map
      mapInstanceRef.current = L.map(mapRef.current, {
        center: [20, 0],
        zoom: 2,
        minZoom: 2,
        maxZoom: 18,
        zoomControl: true,
        scrollWheelZoom: true
      });

      // Add OpenStreetMap tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
        maxZoom: 19
      }).addTo(mapInstanceRef.current);
    }

    // Update markers
    if (mapInstanceRef.current) {
      mapInstanceRef.current.eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          mapInstanceRef.current.removeLayer(layer);
        }
      });

      const bounds = [];
      const customIcon = L.divIcon({
        className: 'custom-marker',
        html: `<svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 0C6.716 0 0 6.716 0 15c0 11.25 15 25 15 25s15-13.75 15-25c0-8.284-6.716-15-15-15z" fill="#E63C23"/>
          <circle cx="15" cy="14" r="5" fill="white"/>
        </svg>`,
        iconSize: [30, 40],
        iconAnchor: [15, 40],
        popupAnchor: [0, -40]
      });

      clientCoordinates.forEach((client) => {
        const lat = parseFloat(client.latitude);
        const lng = parseFloat(client.longitude);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          bounds.push([lat, lng]);
          L.marker([lat, lng], { icon: customIcon })
            .addTo(mapInstanceRef.current)
            .bindPopup(`<div style="font-family: 'Inter', sans-serif; padding: 8px;">
              <strong style="color: #1F2937; font-size: 14px;">${client.client_name || 'Unknown'}</strong>
              <br/><span style="color: #6B7280; font-size: 12px;">📍 ${lat.toFixed(4)}, ${lng.toFixed(4)}</span>
            </div>`);
        }
      });

      if (bounds.length > 0) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
      }
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [clientCoordinates]);

  return (
    <div 
      ref={mapRef} 
      style={{
        height: '300px',
        width: '100%',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid var(--border-color)'
      }}
    />
  );
};

  const renderAdminDashboard = () => {
    return (
      <>
        {/* ADMIN DASHBOARD - 6 Orange Cards with Top Spacing */}
        <div
          className="row g-4 mt-4"
          style={{ marginLeft: 0, marginRight: 0 }} // Inline CSS to remove row margins
        >
          {[
            {
              label: "Total Active Clients",
              value: counts?.total_active_clients ?? 0,
              icon: Users,
              key: "total_active_clients",
            },
            {
              label: "Vehicles Parked Today",
              value: counts?.total_vehicles_parked_today ?? 0,
              icon: Car,
              key: "total_vehicles_parked_today",
            },
            {
              label: "Total Branches",
              value: counts?.total_branches ?? 0,
              icon: Building,
              key: "total_branches",
            },
            {
              label: "Total Locations",
              value: counts?.total_locations ?? 0,
              icon: MapPin,
              key: "total_locations",
            },
            {
              label: "Total Staff",
              value: counts?.total_staff ?? 0,
              icon: Users,
              key: "total_staff",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="col-xl col-lg col-md col-sm col-12"
              style={{ paddingLeft: "8px", paddingRight: "8px" }} // Inline CSS for consistent column spacing
            >
              <div className="card shadow-4 border-0 rounded-8 bg-primary-100 h-100">
                <div className="card-body p-3 p-md-4 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-white opacity-75 text-xs fw-bold">
                      {item.label}
                    </span>
                    <item.icon className="text-white opacity-50" size={20} />
                  </div>
                  <h5 className="text-white fw-bold mb-1 text-md">
                    {item.value}
                  </h5>
                  {/* <span className="d-flex align-items-center gap-1 text-xxs text-white opacity-75">
                    <i
                      className="bi bi-arrow-up"
                      style={{ fontSize: "10px" }}
                    ></i>
                    +8% from last period
                  </span> */}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ADMIN - Middle Row (Revenue Analytics and Client Subscription) */}
        <div className="row g-4 mt-2">
          {/* Revenue Analytics */}
          <div className="col-lg-6 col-md-12">
            {loading ? (
              <ShimmerChart />
            ) : (
              <div className="card shadow-4 ">
                <div className="card-body p-3 p-md-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    
                    <h6 className="mb-0 fw-bold text-lg text-primary-light">
                      Revenue Analytics
                    </h6>
                    <i
                      className="bi bi-info-circle text-secondary-light"
                      style={{ fontSize: "18px", cursor: "pointer" }}
                    ></i>
                  </div>

                  {/* Top Row - Pie and Bar Charts */}
                  <div className="row g-3 mb-3">
                    {/* Left - Revenue by Payment Type Pie Chart */}
                    <div className="col-md-6 col-12">
                      <div className="text-center mb-2">
                        <small className="text-secondary-light fw-semibold text-xs">
                          Revenue by Payment Type
                        </small>
                      </div>
                      <div className="position-relative">
                        {paymentSources.length === 0 ? (
                          <div
                            className="d-flex align-items-center justify-content-center text-secondary-light text-align-center"
                            style={{ height: "220px" }}
                          >
                            No payment data available
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie
                                data={paymentSources}
                                cx="50%"
                                cy="50%"
                                innerRadius={45}
                                outerRadius={75}
                                dataKey="value"
                                startAngle={90}
                                endAngle={450}
                                paddingAngle={2}
                                label={({ name }) => name}
                                labelLine={true}
                              >
                                {paymentSources.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={entry.fill}
                                    stroke="var(--neutral-50)"
                                    strokeWidth={2}
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  background: "var(--neutral-50)",
                                  border: "1px solid var(--border-color)",
                                  borderRadius: "var(--rounded-6)",
                                  fontSize: "var(--font-xs)",
                                }}
                                formatter={(value, name) => [
                                  `${value.toLocaleString()} payments`,
                                  name,
                                ]}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>

                    {/* Right - Monthly Billing Summary Bar Chart */}
                    {/* Right - Monthly Billing Summary Bar Chart */}
                    <div className="col-md-6 col-12">
                      <div className="text-center mb-2">
                        <small className="text-primary-light fw-semibold text-xs">
                          Monthly Billing Summary
                        </small>
                      </div>
                      {monthlyBillingData.length === 0 ? (
                        <div
                          className="d-flex align-items-center justify-content-center text-secondary-light text-align-center"
                          style={{ height: "230px" }}
                        >
                          No billing data available
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height={230}>
                          <BarChart
                            data={monthlyBillingData}
                            margin={{
                              top: 10,
                              right: 10,
                              left: -15,
                              bottom: 5,
                            }}
                            barGap={3} // Subscription aur Invoice ke beech spacing
                            barCategoryGap="25%" // Months ke beech spacing
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="var(--border-color)"
                              vertical={false}
                            />
                            <XAxis
                              dataKey="name"
                              tick={{
                                fill: "var(--text-secondary-light)",
                                fontSize: "var(--font-xs)",
                              }}
                              axisLine={false}
                              tickLine={false}
                            />
                            <YAxis
                              tick={{
                                fill: "var(--text-secondary-light)",
                                fontSize: "var(--font-xs)",
                              }}
                              axisLine={false}
                              tickLine={false}
                              width={35}
                              tickFormatter={(v) =>
                                `$${(v / 1000).toFixed(1)}k`
                              }
                            />
                            <Tooltip
                              contentStyle={{
                                background: "var(--neutral-50)",
                                border: "1px solid var(--border-color)",
                                borderRadius: "var(--rounded-6)",
                                fontSize: "var(--font-xs)",
                              }}
                              formatter={(v, name) => [
                                `$${v.toLocaleString()}`,
                                name === "Subscription"
                                  ? "Subscription"
                                  : "Invoice",
                              ]}
                            />
                            <Legend
                              content={() => (
                                <div className="d-flex justify-content-center gap-4 mt-2">
                                  <div className="d-flex align-items-center gap-1">
                                    <div
                                      style={{
                                        width: 10,
                                        height: 10,
                                        background: "#2F4F4F",
                                        borderRadius: 2,
                                      }}
                                    ></div>
                                    <small className="text-secondary-light">
                                      Subscription
                                    </small>
                                  </div>
                                  <div className="d-flex align-items-center gap-1">
                                    <div
                                      style={{
                                        width: 10,
                                        height: 10,
                                        background: "#B0C4DE",
                                        borderRadius: 2,
                                      }}
                                    ></div>
                                    <small className="text-secondary-light">
                                      Invoice
                                    </small>
                                  </div>
                                </div>
                              )}
                            />
                            {/* DO NOT USE stackId - grouped bars ke liye */}
                            <Bar
                              dataKey="dark"
                              fill="#2F4F4F"
                              radius={[4, 4, 0, 0]}
                              maxBarSize={20}
                              name="Subscription"
                            />
                            <Bar
                              dataKey="light"
                              fill="#B0C4DE"
                              radius={[4, 4, 0, 0]}
                              maxBarSize={20}
                              name="Invoice"
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* code 1 */}
 {/* World Wide Client Locations */}
<div className="col-lg-6 col-md-12">
  {loading ? (
    <ShimmerChart />
  ) : (
    <div className="card shadow-4 ">
      <div className="card-body p-3 p-md-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h6 className="mb-0 fw-bold text-lg text-primary-light">
            World Wide Client Locations
          </h6>
          
        </div>

        {clientCoordinates.length === 0 ? (
          <div className="d-flex flex-column align-items-center justify-content-center text-secondary-light" style={{ height: "300px" }}>
            <i className="bi bi-geo-alt-fill mb-3" style={{ fontSize: "48px", opacity: 0.3 }}></i>
            <p className="mb-0">No client location data available</p>
          </div>
        ) : (
          <>
            <WorldWideClientMap clientCoordinates={clientCoordinates} />
            <style>{`
              .custom-marker { background: none; border: none; }
              .leaflet-popup-content-wrapper { border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
            `}</style>
          </>
        )}
      </div>
    </div>
  )}
</div>
          {/* Client Subscription Table */}
          
        </div>

        {/* ADMIN - Bottom Row (Occupancy Summary, Plan Usage, Staff Activity, Quick Actions) */}
        <div className="row g-4 mt-2">
          {/* Left Column (Occupancy Summary and Plan Usage) */}
          <div className="col-lg-6 col-md-12">
            {/* Occupancy Summary */}
            <div className="mb-2">
  {loading ? (
    <ShimmerChart />
  ) : (
    <div className="card shadow-4 ">
      <div className="card-body p-3 p-md-4">
        {/* UPDATED HEADER WITH DATE RANGE FILTER */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <h6 className="mb-0 fw-bold text-md text-primary-light">
            Top Active Locations
          </h6>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange({
                  ...dateRange,
                  startDate: e.target.value,
                  isRangeSelected: false
                })
              }
              style={{ width: "140px", fontSize: "12px" }}
            />
            <span className="text-secondary-light text-xs">to</span>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateRange.endDate}
              onChange={(e) =>
                setDateRange({
                  ...dateRange,
                  endDate: e.target.value,
                  isRangeSelected: true
                })
              }
              style={{ width: "140px", fontSize: "12px" }}
            />
          </div>
        </div>

        <div className="mb-3">
          <div className="d-flex gap-4 flex-wrap">
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  width: 12,
                  height: 12,
                  background: "linear-gradient(135deg, #2F4F4F, #1a1a2e)",
                  borderRadius: "2px",
                }}
              ></div>
              <small className="text-xs text-secondary-light">
                Occupied Slots
              </small>
            </div>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              background:
                                "linear-gradient(135deg, #87CEEB, #B0C4DE)",
                              borderRadius: "2px",
                            }}
                          ></div>
                          <small className="text-xs text-secondary-light">
                            Available Slots
                          </small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            style={{
                              width: 16,
                              height: 3,
                              background: "#E63C23",
                              borderRadius: "2px",
                            }}
                          ></div>
                          <small className="text-xs text-secondary-light">
                            Occupancy Rate %
                          </small>
                        </div>
                      </div>
                    </div>
                    {topActiveLocations.length === 0 ? (
                      <div
                        className="d-flex align-items-center justify-content-center text-secondary-light text-align-center"
                        style={{ height: "320px" }}
                      >
                        No location data available
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={320}>
                        <ComposedChart
                          data={topActiveLocations.slice(0, 8).map((loc) => ({
                            name:
                              loc.location_name.length > 12
                                ? loc.location_name.substring(0, 12) + "..."
                                : loc.location_name,
                            fullName: loc.location_name,
                            occupied: parseInt(loc.occupied_slots) || 0,
                            available: parseInt(loc.available_slots) || 0,
                            total: parseInt(loc.total_slots) || 0,
                            occupancyRate: parseFloat(loc.occupancy_rate) || 0,
                          }))}
                          margin={{ top: 20, right: 40, left: 10, bottom: 60 }}
                        >
                          <defs>
                            <linearGradient
                              id="occupiedMixedGradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#2F4F4F"
                                stopOpacity={1}
                              />
                              <stop
                                offset="100%"
                                stopColor="#1a1a2e"
                                stopOpacity={0.8}
                              />
                            </linearGradient>
                            <linearGradient
                              id="availableMixedGradient"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor="#87CEEB"
                                stopOpacity={1}
                              />
                              <stop
                                offset="100%"
                                stopColor="#B0C4DE"
                                stopOpacity={0.8}
                              />
                            </linearGradient>
                            <linearGradient
                              id="lineGradientMixed"
                              x1="0"
                              y1="0"
                              x2="1"
                              y2="0"
                            >
                              <stop
                                offset="0%"
                                stopColor="#E63C23"
                                stopOpacity={1}
                              />
                              <stop
                                offset="50%"
                                stopColor="#FF6B4A"
                                stopOpacity={1}
                              />
                              <stop
                                offset="100%"
                                stopColor="#E63C23"
                                stopOpacity={1}
                              />
                            </linearGradient>
                            <filter id="shadowMixed">
                              <feDropShadow
                                dx="0"
                                dy="2"
                                stdDeviation="3"
                                floodColor="#E63C23"
                                floodOpacity="0.3"
                              />
                            </filter>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="var(--border-color)"
                            strokeOpacity={0.3}
                            vertical={false}
                          />
                          <XAxis
                            dataKey="name"
                            angle={0}
                            textAnchor="middle"
                            height={80}
                            interval={0}
                            tick={{
                              fill: "var(--text-secondary-light)",
                              fontSize: "var(--font-xs)",
                              fontWeight: 600,
                            }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis
                            yAxisId="left"
                            tick={{
                              fill: "var(--text-secondary-light)",
                              fontSize: "var(--font-xs)",
                            }}
                            axisLine={false}
                            tickLine={false}
                            label={{
                              value: "Slots",
                              angle: -90,
                              position: "insideLeft",
                              style: {
                                fontSize: "var(--font-xs)",
                                fill: "var(--text-secondary-light)",
                              },
                            }}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            domain={[0, 100]}
                            tick={{
                              fill: "var(--text-secondary-light)",
                              fontSize: "var(--font-xs)",
                            }}
                            axisLine={false}
                            tickLine={false}
                            label={{
                              value: "Occupancy %",
                              angle: 90,
                              position: "insideRight",
                              style: {
                                fontSize: "var(--font-xs)",
                                fill: "var(--text-secondary-light)",
                              },
                            }}
                          />
                          <Tooltip
                            contentStyle={{
                              background: "var(--neutral-50)",
                              border: "1px solid var(--border-color)",
                              borderRadius: "var(--rounded-8)",
                              boxShadow: "var(--shadow-4)",
                              padding: "12px",
                            }}
                            formatter={(value, name, props) => {
                              if (name === "Occupancy Rate") {
                                return [`${value}%`, name];
                              }
                              return [`${value} slots`, name];
                            }}
                            labelFormatter={(label, payload) => {
                              if (payload && payload[0]) {
                                return `📍 ${payload[0].payload.fullName}`;
                              }
                              return label;
                            }}
                          />
                          <Legend
                            wrapperStyle={{ paddingTop: "15px" }}
                            iconType="rect"
                            content={(props) => {
                              const { payload } = props;
                              return (
                                <div className="d-flex justify-content-center gap-3 mt-2 flex-wrap">
                                  {payload.map((entry, index) => (
                                    <div
                                      key={index}
                                      className="d-flex align-items-center gap-2"
                                    >
                                      <div
                                        style={{
                                          width:
                                            entry.value === "Occupancy Rate"
                                              ? 16
                                              : 12,
                                          height:
                                            entry.value === "Occupancy Rate"
                                              ? 3
                                              : 12,
                                          background: entry.color,
                                          borderRadius: "2px",
                                        }}
                                      ></div>
                                      <small className="text-secondary-light fw-semibold text-xs">
                                        {entry.value}
                                      </small>
                                    </div>
                                  ))}
                                </div>
                              );
                            }}
                          />
                          {/* Bars for Occupied Slots */}
                          <Bar
                            yAxisId="left"
                            dataKey="occupied"
                            fill="url(#occupiedMixedGradient)"
                            name="Occupied"
                            radius={[4, 4, 0, 0]}
                            barSize={20}
                          />
                          {/* Bars for Available Slots */}
                          <Bar
                            yAxisId="left"
                            dataKey="available"
                            fill="url(#availableMixedGradient)"
                            name="Available"
                            radius={[4, 4, 0, 0]}
                            barSize={20}
                          />
                          {/* Line for Occupancy Rate */}
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="occupancyRate"
                            stroke="url(#lineGradientMixed)"
                            strokeWidth={3}
                            name="Occupancy Rate"
                            dot={{
                              r: 5,
                              fill: "#E63C23",
                              strokeWidth: 2,
                              stroke: "var(--neutral-50)",
                              filter: "url(#shadowMixed)",
                            }}
                            activeDot={{
                              r: 7,
                              fill: "#E63C23",
                              stroke: "var(--neutral-50)",
                              strokeWidth: 3,
                              filter: "url(#shadowMixed)",
                            }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                    {/* Summary Stats */}
                    {topActiveLocations.length > 0 && (
                      <div
                        className="row g-2 mt-3 pt-3"
                        style={{ borderTop: "1px solid var(--border-color)" }}
                      >
                        <div className="col-3">
                          <div
                            className="text-center p-2 rounded-6"
                            style={{ background: "var(--success-50)" }}
                          >
                            <small className="text-xs text-secondary-light d-block">
                              Total Slots
                            </small>
                            <span className="fw-bold text-primary-light text-sm">
                              {topActiveLocations.reduce(
                                (sum, loc) =>
                                  sum + (parseInt(loc.total_slots) || 0),
                                0
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="col-3">
                          <div
                            className="text-center p-2 rounded-6"
                            style={{ background: "var(--primary-50)" }}
                          >
                            <small className="text-xs text-secondary-light d-block">
                              Avg Occupancy
                            </small>
                            <span className="fw-bold text-primary-light text-sm">
                              {(
                                topActiveLocations.reduce(
                                  (sum, loc) =>
                                    sum + parseFloat(loc.occupancy_rate || 0),
                                  0
                                ) / topActiveLocations.length
                              ).toFixed(1)}
                              %
                            </span>
                          </div>
                        </div>
                        <div className="col-3">
                          <div
                            className="text-center p-2 rounded-6"
                            style={{ background: "var(--warning-50)" }}
                          >
                            <small className="text-xs text-secondary-light d-block">
                              Peak Location
                            </small>
                            <span className="fw-bold text-primary-light text-xs">
                              {topActiveLocations
                                .reduce((max, loc) =>
                                  parseFloat(loc.occupancy_rate || 0) >
                                  parseFloat(max.occupancy_rate || 0)
                                    ? loc
                                    : max
                                )
                                .location_name.substring(0, 8)}
                              ...
                            </span>
                          </div>
                        </div>
                        <div className="col-3">
                          <div
                            className="text-center p-2 rounded-6"
                            style={{ background: "var(--info-50)" }}
                          >
                            <small className="text-xs text-secondary-light d-block">
                              Total Occupied
                            </small>
                            <span className="fw-bold text-primary-light text-sm">
                              {topActiveLocations.reduce(
                                (sum, loc) =>
                                  sum + (parseInt(loc.occupied_slots) || 0),
                                0
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Plan Usage Statistics with Top Spacing */}
            <div style={{ marginTop: "24px" }}>
             <div className="col">
            {loading ? (
              <ShimmerTable />
            ) : (
              <div className="card shadow-4 ">
                <div
                  className="card-body p-3 p-md-4"
                  style={{ paddingBottom: "0.75rem" }}
                >
                  {/* Updated Header with Date Pickers */}
                  <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                    <h6 className="mb-0 fw-bold text-md text-primary-light">
                      Top 10 Active Clients
                    </h6>
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={dateRange.startDate}
                        onChange={(e) =>
                          setDateRange({
                            ...dateRange,
                            startDate: e.target.value,
                             isRangeSelected: false
                          })
                        }
                        style={{ width: "140px", fontSize: "12px" }}
                      />
                      <span className="text-secondary-light text-xs">to</span>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={dateRange.endDate}
                        onChange={(e) =>
                          setDateRange({
                            ...dateRange,
                            endDate: e.target.value,
                            isRangeSelected: true
                          })
                        }
                        style={{ width: "140px", fontSize: "12px" }}
                      />
                    </div>
                  </div>

                  {/* Table (unchanged) */}
                  <div
                    className="table-responsive"
                    style={{ marginBottom: "0" }}
                  >
                    <table className="table bordered-table sm-table mb-0">
                      <thead>
                        <tr>
                          <th className="text-xs text-secondary-light">
                            Client
                          </th>
                          <th className="text-xs text-secondary-light text-center">
                            Active
                          </th>
                          <th className="text-xs text-secondary-light text-center">
                            Plan
                          </th>
                          <th className="text-xs text-secondary-light text-center">
                            Vehicles
                          </th>
                          <th className="text-xs text-secondary-light text-center">
                            Revenue
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {topActiveClients.length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-center text-secondary-light"
                            >
                              No active clients data available
                            </td>
                          </tr>
                        ) : (
                          topActiveClients.slice(0, 10).map((row, i) => (
                            <tr key={i}>
                              <td className="text-xs fw-semibold text-primary-light">
                                {row.client_name || "Unknown"}
                              </td>
                              <td className="text-center">
                                <span className="badge bg-success-600 text-white text-xxs">
                                  {row.total_instances > 0
                                    ? "Active"
                                    : "Inactive"}
                                </span>
                              </td>
                              <td className="text-center">
                                <span
                                  className="badge text-white text-xxs"
                                  style={{
                                    background:
                                      row.plan_name.includes("Enterprise") ||
                                      row.plan_name.includes("Growth")
                                        ? "var(--primary-600)"
                                        : row.plan_name.includes("Starter")
                                        ? "var(--info-600)"
                                        : "var(--warning-600)",
                                  }}
                                >
                                  {row.plan_name || "N/A"}
                                </span>
                              </td>
                              <td className="text-xs text-secondary-light text-center">
                                {row.total_vehicles || 0}
                              </td>
                              <td className="text-xs fw-bold text-primary-light text-center">
                                {new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: row.currency || "USD",
                                  currencyDisplay: "code",
                                }).format(row.total_revenue || 0)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
            </div>
          </div>

          {/* Right Column (Staff Activity and Quick Actions) */}
          <div className="col-lg-6 col-md-12">
            {/* Staff & Branch Activity */}
<div className="mb-4">
  {loading ? (
    <ShimmerChart />
  ) : (
    <div className="card shadow-4 ">
      <div className="card-body p-3 p-md-4">
        <div className="d-flex align-items-start justify-content-between mb-3">
          <div>
            <h6 className="mb-1 fw-bold text-primary-light text-md">
              Staff & Branch Activity
            </h6>
            <p className="mb-0 text-secondary-light text-sm">
              Staff Logged-in Today (Per Client)
            </p>
          </div>
          <i
            className="bi bi-info-circle text-secondary-light"
            style={{ fontSize: "20px", cursor: "pointer" }}
          ></i>
        </div>

        <div
          className="position-relative mb-3"
          style={{ marginTop: "20px", height: "250px" }}
        >
          {staffLoginData.length === 0 ? (
            <div
              className="d-flex align-items-center justify-content-center text-secondary-light text-align-center"
              style={{ height: "200px" }}
            >
              No staff login data available for today
            </div>
          ) : (
            <div
              style={{
                overflowX: "auto",
                overflowY: "hidden",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}
              onWheel={(e) => {
                const container = e.currentTarget;
                if (e.deltaY !== 0) {
                  container.scrollLeft += e.deltaY;
                  e.preventDefault();
                }
              }}
            >
              <div
                style={{
                  width: `${staffLoginData.length * 120}px`,
                  height: "250px",
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={staffLoginData}
                    margin={{ top: 10, right: 20, left: 10, bottom: 20 }}
                    barGap={10}
                    barCategoryGap="30%"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="client_name"
                      tick={{
                        fontSize: 12,
                        fill: "var(--text-primary-light)",
                        dy: 10,
                      }}
                      interval={0}
                      angle={0}
                      textAnchor="middle"
                      height={40}
                    />
                    <YAxis
                      tick={{
                        fontSize: 11,
                        fill: "var(--text-primary-light)",
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: "8px",
                        borderColor: "var(--border-color)",
                        boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                      }}
                      formatter={(value, name, props) => {
                        if (name === "Total Staff") return [`${value}`, "Total Staff"];
                        if (name === "Active Today") {
                          return [
                            `${value} (${props.payload.login_ratio_percent}%)`,
                            "Active Today",
                          ];
                        }
                        return value;
                      }}
                    />
                    <Bar
                      dataKey="total_staff"
                      name="Total Staff"
                      fill="#0df2f2ff"
                      radius={[4, 4, 0, 0]}
                      barSize={18}
                    >
                      <LabelList
                        dataKey="total_staff"
                        position="top"
                        fontSize={10}
                        fill="var(--text-primary-light)"
                      />
                    </Bar>
                    <Bar
                      dataKey="today_active_staff"
                      name="Active Today"
                      fill="#eb4d04ff"
                      radius={[4, 4, 0, 0]}
                      barSize={18}
                    >
                      <LabelList
                        dataKey="login_ratio_percent"
                        position="top"
                        formatter={(val) => `${val}%`}
                        fontSize={10}
                        fill="#f3f4f4ff"
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )}
</div>

            {/* Quick Actions with Top Spacing */}
            <div style={{ marginTop: "24px" }}>
              {loading ? (
                <ShimmerChart />
              ) : (
                <div className="card shadow-4">
                  <div className="card-body p-3 p-md-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h6 className="mb-0 fw-bold text-md text-primary-light">
                        Quick Actions
                      </h6>
                    </div>
                    <div className="row g-3 justify-content-center">
                      {" "}
                      {/* justify-content-center add kiya hai */}
                      <div className="col-md-8 col-12">
                        {" "}
                        {/* col-md-6 se col-md-8 kiya aur center kiya */}
                        <div className="row g-2 justify-content-center">
                          {" "}
                          {/* justify-content-center add kiya */}
                          {[
                            {
                              icon: "bi-people-fill",
                              text: "Manage Clients",
                              path: "/all-clients",
                            },
                            {
                              icon: "bi-currency-dollar",
                              text: "Add Pricing Plan",
                              path: "/all-plans",
                            },
                            {
                              icon: "bi-receipt",
                              text: "View All Invoices",
                              path: "/view_billing_summary",
                            },
                            {
                              icon: "bi-list-check",
                              text: "View System Logs",
                              path: "#",
                            },
                          ].map((item, i) => (
                            <div key={i} className="col-md-6 col-12">
                              {" "}
                              {/* col-6 se col-md-6 col-12 kiya */}
                              <button
                                className="btn btn-primary-600 text-white w-100 d-flex align-items-center justify-content-center gap-2 p-2 rounded-6 text-sm fw-medium"
                                onClick={() => navigate(item.path)}
                              >
                                <i className={`bi ${item.icon}`}></i>
                                <span>{item.text}</span>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderClientDashboard = () => {
    return (
      <>
        {/* CLIENT SELECTOR */}
        <div
          className="card shadow-4 border-0 mb-4"
          style={{ border: "2px solid var(--border-card)" }}
        >
          <div className="card-body p-24">
            <div className="client-selector d-flex align-items-center gap-2">
              <label className="form-label mb-0 text-sm text-secondary-light">
                Select Client:
              </label>
              {clientsLoading ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <div style={{ width: "200px" }}>
                  <CustomSelect
                    options={clientsList.map((client) => ({
                      label: client.client_name || `Client ${client.client_id}`,
                      value: client.client_id,
                    }))}
                    value={
                      selectedClient
                        ? {
                            label:
                              selectedClient.client_name ||
                              `Client ${selectedClient.client_id}`,
                            value: selectedClient.client_id,
                          }
                        : null
                    }
                    onChange={(selected) => {
                      const client = clientsList.find(
                        (c) => c.client_id === selected?.value
                      );
                      setSelectedClient(client);
                    }}
                    placeholder="Choose a client..."
                    isSearchable={true}
                    isClearable={true}
                    styles={customSelectStyles}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CLIENT DASHBOARD CONTENT - PURANA WALA CODE YAHI RAHEGA */}
        {!selectedClient ? (
          <div className="col-12">
            <div
              className="card shadow-4 "
              style={{ border: "2px solid var(--border-card)" }}
            >
              <div className="card-body text-center py-5">
                <i className="bi bi-person-plus fs-1 text-secondary-light mb-3"></i>
                <h5 className="text-secondary-light">
                  Please select a client to view their dashboard
                </h5>
                <p className="text-secondary-light">
                  Choose a client from the dropdown above to see their specific
                  data and analytics.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="row gy-4">
            {loading ? (
              [...Array(4)].map((_, i) => (
                <div key={i} className="col-xl-3 col-lg-6 col-md-6 col-12">
                  <ShimmerCard />
                </div>
              ))
            ) : (
              <>
                <div className="col-12 d-flex justify-content-end mb-16">
                  <div style={{ width: "200px" }}>
                    <CustomSelect
                      options={timeFrameOptions}
                      value={timeFrameOptions.find(
                        (opt) => opt.value === timeFrame
                      )}
                      onChange={(selected) =>
                        setTimeFrame(selected?.value || "today")
                      }
                      placeholder="Select Period"
                      isSearchable={false}
                      isClearable={false}
                      styles={customSelectStyles}
                    />
                  </div>
                </div>
                {[
                  {
                    label: `Total Vehicles Parked ${
                      timeFrame === "today"
                        ? "Today"
                        : timeFrame === "weekly"
                        ? "This Week"
                        : "This Month"
                    }`,
                    value: initialData?.vehicles_parked ?? 0,
                    img: carImage,
                    trend: "+5%",
                    trendClass: "text-success-600",
                  },
                  {
                    label: `Current Occupancy ${
                      timeFrame === "today"
                        ? "Today"
                        : timeFrame === "weekly"
                        ? "This Week"
                        : "This Month"
                    }`,
                    value: initialData?.current_occupancy ?? 0,
                    img: usersImage,
                    trend: "-2%",
                    trendClass: "text-danger-600",
                  },
                  {
                    label: `Active Sessions ${
                      timeFrame === "today"
                        ? "Today"
                        : timeFrame === "weekly"
                        ? "This Week"
                        : "This Month"
                    }`,
                    value: initialData?.active_sessions ?? 0,
                    img: buildingImage,
                    trend: "+3%",
                    trendClass: "text-success-600",
                  },
                  {
                    label: `Revenue ${
                      timeFrame === "today"
                        ? "Today"
                        : timeFrame === "weekly"
                        ? "This Week"
                        : "This Month"
                    }`,
                    value: new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency:
                        initialData?.revenue_generated?.[0]?.currency_code ||
                        "USD",
                    }).format(initialData?.revenue_generated?.[0]?.total ?? 0),
                    img: locationImage,
                    trend: "-3%",
                    trendClass: "text-danger-600",
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="col-xl-3 col-lg-6 col-md-6 col-12"
                  >
                    <div
                      className="card shadow-4 h-100"
                      style={{ border: "2px solid var(--border-card)" }}
                    >
                      <div className="card-body p-3 d-flex align-items-center">
                        <div
                          className="d-flex justify-content-center align-items-center rounded-circle me-3"
                          style={{ width: "60px", height: "60px" }}
                        >
                          <img
                            src={item.img}
                            alt={item.label}
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                        <div className="text-start">
                          <p className="fw-bold mb-1 text-secondary-light text-xs">
                            {item.label}
                          </p>
                          <h5 className="fw-bold mb-1 text-primary-light text-md">
                            {item.value}
                          </h5>
                          <span
                            className={`d-flex align-items-center gap-1 ${item.trendClass} text-xxs`}
                          >
                            <i
                              className={`bi bi-arrow-${
                                item.trend.includes("+") ? "up" : "down"
                              }`}
                              style={{ fontSize: "8px" }}
                            ></i>
                            {item.trend} from last period
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            <div className="row gy-4 mt-4">
              <div className="col-lg-6 col-12">
                {loading ? (
                  <ShimmerChart />
                ) : (
                  <div
                    className="card shadow-4 h-100 rounded-8"
                    style={{ border: "2px solid var(--border-card)" }}
                  >
                    <div className="card-body p-24">
                      <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
                        <h6 className="mb-0 fw-bold text-md text-primary-light">
                          Occupancy Overview
                        </h6>
                        <div className="btn-group">
                          <button
                            className={`btn btn-primary-600 ${
                              activeOccupancyTab === "byLot" ? "" : "active"
                            }`}
                            onClick={() => setActiveOccupancyTab("byLot")}
                          >
                            By Location
                          </button>
                          <button
                            className={`btn btn-primary-600 ${
                              activeOccupancyTab === "hourly" ? "" : "active"
                            }`}
                            onClick={() => setActiveOccupancyTab("hourly")}
                          >
                            Hourly Trend
                          </button>
                        </div>
                      </div>
                      <div className="chart-container">
                        {activeOccupancyTab === "byLot" ? (
                          <div style={{ minHeight: "360px" }}>
                            {occupancyData.lotData.length === 0 ? (
                              <div className="d-flex align-items-center justify-content-center h-100 text-secondary-light text-align-center">
                                No location data available
                              </div>
                            ) : (
                              <ResponsiveContainer width="100%" height={360}>
                                <BarChart
                                  data={occupancyData.lotData.map((item) => ({
                                    name: item.name
                                      .replace("Lot ", "")
                                      .replace(" - ", "\n"),
                                    occupied: item.occupied,
                                    available: item.total - item.occupied,
                                    percentage: item.percentage,
                                    total: item.total,
                                  }))}
                                  margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 60,
                                  }}
                                >
                                  <defs>
                                    <linearGradient
                                      id="occupiedGradient"
                                      x1="0"
                                      y1="0"
                                      x2="0"
                                      y2="1"
                                    >
                                      <stop
                                        offset="5%"
                                        stopColor="#E63C23"
                                        stopOpacity={0.8}
                                      />
                                      <stop
                                        offset="95%"
                                        stopColor="#E63C23"
                                        stopOpacity={0.3}
                                      />
                                    </linearGradient>
                                    <linearGradient
                                      id="availableGradient"
                                      x1="0"
                                      y1="0"
                                      x2="0"
                                      y2="1"
                                    >
                                      <stop
                                        offset="5%"
                                        stopColor="#FF6B4A"
                                        stopOpacity={0.8}
                                      />
                                      <stop
                                        offset="95%"
                                        stopColor="#FF6B4A"
                                        stopOpacity={0.3}
                                      />
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="var(--border-color)"
                                    strokeOpacity={0.3}
                                  />
                                  <XAxis
                                    dataKey="name"
                                    tick={{
                                      fill: "var(--text-secondary-light)",
                                      fontSize: "var(--font-sm)",
                                    }}
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                    interval={0}
                                  />
                                  <YAxis
                                    tick={{
                                      fill: "var(--text-secondary-light)",
                                      fontSize: "var(--font-sm)",
                                    }}
                                  />
                                  <Tooltip
                                    contentStyle={{
                                      background: "var(--neutral-50)",
                                      border: "1px solid var(--border-color)",
                                      borderRadius: "var(--rounded-8)",
                                      boxShadow: "var(--shadow-4)",
                                    }}
                                    formatter={(value, name) => {
                                      if (name === "occupied")
                                        return [`${value} spaces`, "Occupied"];
                                      if (name === "available")
                                        return [`${value} spaces`, "Available"];
                                      return [value, name];
                                    }}
                                    labelFormatter={(label) =>
                                      `Location: ${label}`
                                    }
                                  />
                                  <Legend />
                                  <Bar
                                    dataKey="occupied"
                                    stackId="a"
                                    fill="url(#occupiedGradient)"
                                    name="Occupied"
                                    radius={[0, 0, 0, 0]}
                                  />
                                  <Bar
                                    dataKey="available"
                                    stackId="a"
                                    fill="url(#availableGradient)"
                                    name="Available"
                                    radius={[4, 4, 0, 0]}
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        ) : (
                          <div style={{ minHeight: "360px" }}>
                            {occupancyData.hourlyData.length === 0 ? (
                              <div className="d-flex align-items-center justify-content-center h-100 text-secondary-light text-align-center">
                                No hourly data available
                              </div>
                            ) : (
                              <ResponsiveContainer width="100%" height={360}>
                                <AreaChart
                                  data={occupancyData.hourlyData}
                                  margin={{
                                    top: 20,
                                    right: 30,
                                    left: 10,
                                    bottom: 10,
                                  }}
                                >
                                  <defs>
                                    <linearGradient
                                      id="occupancyAreaGradient"
                                      x1="0"
                                      y1="0"
                                      x2="0"
                                      y2="1"
                                    >
                                      <stop
                                        offset="5%"
                                        stopColor="#E63C23"
                                        stopOpacity={0.6}
                                      />
                                      <stop
                                        offset="50%"
                                        stopColor="#FF6B4A"
                                        stopOpacity={0.3}
                                      />
                                      <stop
                                        offset="95%"
                                        stopColor="#FF6B4A"
                                        stopOpacity={0.1}
                                      />
                                    </linearGradient>
                                    <filter id="shadow">
                                      <feDropShadow
                                        dx="0"
                                        dy="2"
                                        stdDeviation="4"
                                        floodColor="#E63C23"
                                        floodOpacity="0.2"
                                      />
                                    </filter>
                                  </defs>
                                  <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="var(--border-color)"
                                    strokeOpacity={0.3}
                                  />
                                  <XAxis
                                    dataKey="hour"
                                    tick={{
                                      fill: "var(--text-secondary-light)",
                                      fontSize: "var(--font-sm)",
                                    }}
                                    axisLine={false}
                                    tickLine={false}
                                  />
                                  <YAxis
                                    tick={{
                                      fill: "var(--text-secondary-light)",
                                      fontSize: "var(--font-sm)",
                                    }}
                                    domain={[0, "dataMax + 1"]}
                                    axisLine={false}
                                    tickLine={false}
                                    allowDecimals={false}
                                  />
                                  <Tooltip
                                    contentStyle={{
                                      background: "var(--neutral-50)",
                                      border: "1px solid var(--border-color)",
                                      borderRadius: "var(--rounded-8)",
                                      boxShadow: "var(--shadow-4)",
                                    }}
                                    formatter={(v, name, props) => [
                                      `${v} vehicles`,
                                      props.payload.isPeak
                                        ? "Peak Occupancy"
                                        : "Normal Occupancy",
                                    ]}
                                    labelFormatter={(hour) => `Time: ${hour}`}
                                  />
                                  <Legend
                                    content={() => (
                                      <div className="d-flex justify-content-center gap-4 mt-3">
                                        <div className="d-flex align-items-center gap-2">
                                          <div
                                            style={{
                                              width: 12,
                                              height: 12,
                                              background: "var(--primary-600)",
                                              borderRadius: "50%",
                                              boxShadow:
                                                "0 0 8px var(--primary-600)40",
                                            }}
                                          ></div>
                                          <small className="text-secondary-light">
                                            Hourly Occupancy
                                          </small>
                                        </div>
                                      </div>
                                    )}
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#E63C23"
                                    strokeWidth={3}
                                    fill="url(#occupancyAreaGradient)"
                                    dot={{
                                      r: 4,
                                      fill: "#E63C23",
                                      strokeWidth: 2,
                                      stroke: "var(--neutral-50)",
                                      filter: "url(#shadow)",
                                    }}
                                    activeDot={{
                                      r: 6,
                                      fill: "#E63C23",
                                      stroke: "var(--neutral-50)",
                                      strokeWidth: 3,
                                      filter: "url(#shadow)",
                                    }}
                                  />
                                </AreaChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-lg-6 col-12">
                {loading ? (
                  <ShimmerChart />
                ) : (
                  <div
                    className="card shadow-4 h-100 rounded-8"
                    style={{ border: "2px solid var(--border-card)" }}
                  >
                    <div className="card-body p-24">
                      <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
                        <h6 className="mb-0 fw-bold text-md text-primary-light">
                          Revenue Analytics
                        </h6>
                        <div className="btn-group">
                          <button
                            className={`btn btn-primary-600 ${
                              activeRevenueTab === "dailyTrend" ? "" : "active"
                            }`}
                            onClick={() => setActiveRevenueTab("dailyTrend")}
                          >
                            Daily Trend
                          </button>
                          <button
                            className={`btn btn-primary-600 ${
                              activeRevenueTab === "paymentTypes"
                                ? ""
                                : "active"
                            }`}
                            onClick={() => setActiveRevenueTab("paymentTypes")}
                          >
                            Payment Sources
                          </button>
                        </div>
                      </div>
                      <div className="chart-container">
                        {activeRevenueTab === "paymentTypes" ? (
                          <div style={{ minHeight: "360px" }}>
                            {processedPaymentData.length === 0 ? (
                              <div
                                className="d-flex align-items-center justify-content-center text-secondary-light text-align-center"
                                style={{ minHeight: "360px" }}
                              >
                                No payment data available
                              </div>
                            ) : (
                              <div className="row h-100">
                                <div className="col-lg-7 col-12">
                                  <ResponsiveContainer
                                    width="100%"
                                    height={280}
                                  >
                                    <PieChart>
                                      <Pie
                                        data={processedPaymentData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={110}
                                        paddingAngle={3}
                                        dataKey="value"
                                      >
                                        {processedPaymentData.map(
                                          (entry, index) => (
                                            <Cell
                                              key={`cell-${index}`}
                                              fill={entry.fill}
                                              stroke="var(--neutral-50)"
                                              strokeWidth={2}
                                            />
                                          )
                                        )}
                                      </Pie>
                                      <Tooltip
                                        contentStyle={{
                                          background: "var(--neutral-50)",
                                          border:
                                            "1px solid var(--border-color)",
                                          borderRadius: "var(--rounded-12)",
                                          boxShadow: "var(--shadow-4)",
                                        }}
                                        formatter={(value, name) => [
                                          `$${value.toLocaleString()}`,
                                          name,
                                        ]}
                                      />
                                    </PieChart>
                                  </ResponsiveContainer>
                                </div>
                                <div className="col-lg-5 col-12 d-flex flex-column justify-content-center">
                                  <div className="mb-4 text-align-center">
                                    <h6 className="fw-bold mb-1 text-md text-primary-light">
                                      Total Revenue
                                    </h6>
                                    <h3
                                      className="fw-bold mb-0"
                                      style={{
                                        fontSize: "var(--font-2xl)",
                                        background:
                                          "linear-gradient(135deg, var(--primary-600), var(--success-600))",
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                      }}
                                    >
                                      $
                                      {new Intl.NumberFormat("en-US").format(
                                        revenueData.paymentTypes
                                          .total_revenue || 0
                                      )}
                                    </h3>
                                  </div>
                                  <div className="d-flex flex-column gap-3">
                                    {processedPaymentData.map((item, index) => (
                                      <div
                                        key={index}
                                        className="d-flex align-items-center justify-content-between p-3"
                                        style={{
                                          background:
                                            "linear-gradient(135deg, var(--neutral-50)10, var(--neutral-50)05)",
                                          borderRadius: "var(--rounded-12)",
                                          border:
                                            "1px solid var(--border-color)",
                                          transition: "all 0.3s ease",
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.transform =
                                            "translateY(-2px)";
                                          e.currentTarget.style.boxShadow =
                                            "var(--shadow-4)";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.transform =
                                            "translateY(0)";
                                          e.currentTarget.style.boxShadow =
                                            "none";
                                        }}
                                      >
                                        <div className="d-flex align-items-center gap-3">
                                          <div
                                            style={{
                                              width: "12px",
                                              height: "12px",
                                              borderRadius: "50%",
                                              background: item.fill,
                                              boxShadow: `0 0 10px ${item.fill}40`,
                                            }}
                                          ></div>
                                          <span className="fw-semibold text-sm text-primary-light">
                                            {item.name}
                                          </span>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                          <span className="fw-bold text-sm text-primary-light">
                                            ${item.value.toLocaleString()}
                                          </span>
                                          <span
                                            className="badge"
                                            style={{
                                              background: `${item.fill}20`,
                                              color: item.fill,
                                              fontSize: "var(--font-xs)",
                                              padding: "4px 8px",
                                              borderRadius: "var(--rounded-6)",
                                              border: `1px solid ${item.fill}40`,
                                            }}
                                          >
                                            {item.percentage.toFixed(1)}%
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ minHeight: "360px" }}>
                            {processedDailyData.length === 0 ? (
                              <div
                                className="d-flex align-items-center justify-content-center text-secondary-light text-align-center"
                                style={{ minHeight: "360px" }}
                              >
                                No daily trend data available
                              </div>
                            ) : (
                              <>
                                <div className="row mb-4">
                                  <div className="col-4">
                                    <div
                                      className="text-align-center p-3"
                                      style={{
                                        background:
                                          "linear-gradient(135deg, var(--success-50), var(--success-100))",
                                        borderRadius: "var(--rounded-12)",
                                        border:
                                          "1px solid var(--success-border)",
                                      }}
                                    >
                                      <div className="text-secondary-light text-xs">
                                        Avg Daily
                                      </div>
                                      <div className="fw-bold text-primary-light text-md">
                                        $
                                        {(
                                          processedDailyData.reduce(
                                            (sum, item) => sum + item.value,
                                            0
                                          ) / processedDailyData.length
                                        ).toLocaleString()}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-4">
                                    <div
                                      className="text-align-center p-3"
                                      style={{
                                        background:
                                          "linear-gradient(135deg, var(--primary-50), var(--primary-100))",
                                        borderRadius: "var(--rounded-12)",
                                        border:
                                          "1px solid var(--primary-600)40",
                                      }}
                                    >
                                      <div className="text-secondary-light text-xs">
                                        Peak Day
                                      </div>
                                      <div className="fw-bold text-primary-light text-md">
                                        $
                                        {Math.max(
                                          ...processedDailyData.map(
                                            (item) => item.value
                                          )
                                        ).toLocaleString()}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-4">
                                    <div
                                      className="text-align-center p-3"
                                      style={{
                                        background:
                                          "linear-gradient(135deg, var(--warning-50), var(--warning-100))",
                                        borderRadius: "var(--rounded-12)",
                                        border:
                                          "1px solid var(--warning-600)40",
                                      }}
                                    >
                                      <div className="text-secondary-light text-xs">
                                        Total
                                      </div>
                                      <div className="fw-bold text-primary-light text-md">
                                        $
                                        {processedDailyData
                                          .reduce(
                                            (sum, item) => sum + item.value,
                                            0
                                          )
                                          .toLocaleString()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <ResponsiveContainer width="100%" height={260}>
                                  <LineChart
                                    data={processedDailyData}
                                    margin={{
                                      top: 20,
                                      right: 30,
                                      left: 10,
                                      bottom: 10,
                                    }}
                                  >
                                    <defs>
                                      <linearGradient
                                        id="lineGradient"
                                        x1="0"
                                        y1="0"
                                        x2="1"
                                        y2="0"
                                      >
                                        <stop
                                          offset="0%"
                                          stopColor="#E63C23"
                                          stopOpacity={1}
                                        />
                                        <stop
                                          offset="100%"
                                          stopColor="#FF6B4A"
                                          stopOpacity={1}
                                        />
                                      </linearGradient>
                                      <filter id="glow">
                                        <feGaussianBlur
                                          stdDeviation="3"
                                          result="coloredBlur"
                                        />
                                        <feMerge>
                                          <feMergeNode in="coloredBlur" />
                                          <feMergeNode in="SourceGraphic" />
                                        </feMerge>
                                      </filter>
                                    </defs>
                                    <CartesianGrid
                                      strokeDasharray="3 3"
                                      stroke="var(--border-color)"
                                      strokeOpacity={0.3}
                                    />
                                    <XAxis
                                      dataKey="name"
                                      tick={{
                                        fill: "var(--text-secondary-light)",
                                        fontSize: "var(--font-sm)",
                                      }}
                                      axisLine={false}
                                      tickLine={false}
                                    />
                                    <YAxis
                                      tick={{
                                        fill: "var(--text-secondary-light)",
                                        fontSize: "var(--font-sm)",
                                      }}
                                      tickFormatter={(v) => `$${v / 1000}k`}
                                      axisLine={false}
                                      tickLine={false}
                                    />
                                    <Tooltip
                                      contentStyle={{
                                        background: "var(--neutral-50)",
                                        border: "1px solid var(--border-color)",
                                        borderRadius: "var(--rounded-12)",
                                        boxShadow: "var(--shadow-4)",
                                      }}
                                      formatter={(v) => [
                                        `$${v.toLocaleString()}`,
                                        "Revenue",
                                      ]}
                                      labelStyle={{
                                        color: "var(--text-primary-light)",
                                      }}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="value"
                                      stroke="url(#lineGradient)"
                                      strokeWidth={4}
                                      dot={{
                                        r: 6,
                                        fill: "#E63C23",
                                        strokeWidth: 3,
                                        stroke: "var(--neutral-50)",
                                        filter: "url(#glow)",
                                      }}
                                      activeDot={{
                                        r: 8,
                                        fill: "#E63C23",
                                        stroke: "var(--neutral-50)",
                                        strokeWidth: 4,
                                        filter: "url(#glow)",
                                      }}
                                    />
                                  </LineChart>
                                </ResponsiveContainer>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-lg-6 col-12">
                {loading ? (
                  <ShimmerChart />
                ) : (
                  <div
                    className="card shadow-4 h-100 rounded-8"
                    style={{ border: "2px solid var(--border-card)" }}
                  >
                    <div className="card-body p-24">
                      <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
                        <h6 className="mb-0 fw-bold text-md text-primary-light">
                          Staff Activity
                        </h6>
                      </div>
                      <div className="chart-container">
                        {staffData.length === 0 ? (
                          <div
                            className="d-flex align-items-center justify-content-center text-secondary-light text-align-center"
                            style={{ minHeight: "300px" }}
                          >
                            No staff activity data available
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                              data={staffData.map((staff) => ({
                                name: staff.staff_name || "Unknown",
                                jobs: parseInt(staff.total_jobs) || 0,
                              }))}
                              margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 80,
                              }}
                            >
                              <defs>
                                <linearGradient
                                  id="staffGradient"
                                  x1="0"
                                  y1="0"
                                  x2="0"
                                  y2="1"
                                >
                                  <stop
                                    offset="5%"
                                    stopColor="#E63C23"
                                    stopOpacity={0.8}
                                  />
                                  <stop
                                    offset="95%"
                                    stopColor="#FF6B4A"
                                    stopOpacity={0.6}
                                  />
                                </linearGradient>
                              </defs>
                              <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="var(--border-color)"
                                strokeOpacity={0.3}
                              />
                              <XAxis
                                dataKey="name"
                                tick={{
                                  fill: "var(--text-primary-light)",
                                  fontSize: "var(--font-sm)",
                                }}
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                interval={0}
                              />
                              <YAxis
                                tick={{
                                  fill: "var(--text-secondary-light)",
                                  fontSize: "var(--font-sm)",
                                }}
                                axisLine={false}
                                tickLine={false}
                              />
                              <Tooltip
                                contentStyle={{
                                  background: "var(--neutral-50)",
                                  border: "1px solid var(--border-color)",
                                  borderRadius: "var(--rounded-8)",
                                  boxShadow: "var(--shadow-4)",
                                }}
                                formatter={(value) => [value, "Jobs Completed"]}
                              />
                              <Bar
                                dataKey="jobs"
                                fill="url(#staffGradient)"
                                radius={[4, 4, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <style>{`
        :root {
          --primary-600: #E63C23;
          --primary-100: #FF6B4A;
          --success-600: #10B981;
          --success-50: #ECFDF5;
          --success-100: #D1FAE5;
          --success-border: #6EE7B7;
          --warning-600: #F59E0B;
          --warning-50: #FFFBEB;
          --warning-100: #FEF3C7;
          --danger-600: #EF4444;
          --neutral-50: #F9FAFB;
          --neutral-700: #374151;
          --border-color: #E5E7EB;
          --border-card: #E5E7EB;
          --shadow-4: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
          --font-primary: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          --rounded-8: 8px;
          --rounded-12: 12px;
          --font-xs: 12px;
          --font-sm: 14px;
          --font-md: 16px;
          --font-lg: 18px;
          --font-xl: 20px;
          --font-2xl: 24px;
          --text-primary-light: #1F2937;
          --text-secondary-light: #6B7280;
        }

        [data-theme="dark"] {
          --primary-600: #FF6B4A;
          --primary-100: #E63C23;
          --success-600: #34D399;
          --success-50: #1F2937;
          --success-100: #1F2937;
          --success-border: #34D399;
          --warning-600: #FBBF24;
          --warning-50: #1F2937;
          --warning-100: #1F2937;
          --danger-600: #F87171;
          --neutral-50: #1F2937;
          --neutral-700: #4B5563;
          --border-color: #4B5563;
          --border-card: #4B5563;
          --shadow-4: 0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -2px rgba(0, 0, 0, 0.2);
          --text-primary-light: #F9FAFB;
          --text-secondary-light: #9CA3AF;
        }

        body {
          font-family: var(--font-primary);
          font-size: var(--font-md);
          line-height: 1.5;
        }

        .card {
          background: var(--neutral-50);
          border-radius: var(--rounded-8);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-4);
        }

        .card-body {
          padding: 24px;
        }

        .shimmer {
          background: linear-gradient(
            90deg,
            #f5f5f5 0%,
            #e0e0e0 20%,
            #d0d0d0 40%,
            #f5f5f5 60%
          );
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite linear;
          border-radius: 4px;
          position: relative;
          z-index: 1;
        }

        [data-theme="dark"] .shimmer {
          background: linear-gradient(
            90deg,
            #333333 0%,
            #444444 20%,
            #555555 40%,
            #333333 60%
          );
        }

        @keyframes shimmer {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }

        .btn-group .btn {
          background: var(--neutral-50);
          color: var(--text-secondary-light);
          border: 1px solid var(--border-color);
          border-radius: var(--rounded-8);
          padding: 8px 16px;
          transition: all 0.3s ease;
        }

        .btn-group .btn.active {
          background: var(--primary-600);
          color: #FFFFFF;
          border-color: var(--primary-600);
        }

        .btn-group .btn:hover {
          background: var(--primary-100);
          color: #FFFFFF;
        }

        .modal-content {
          background: var(--neutral-50);
          border-radius: var(--rounded-12);
          border: none;
        }

        .modal-header {
          border-bottom: 1px solid var(--border-color);
        }

        .modal-footer {
          border-top: 1px solid var(--border-color);
        }

        .btn-modal {
          border-radius: var(--rounded-8);
          padding: 8px 16px;
          transition: all 0.3s ease;
        }

        .btn-modal:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-4);
        }

        .client-selector {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .client-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Custom Scrollbar for Staff Chart */
.overflow-auto::-webkit-scrollbar {
  height: 6px;
}

.overflow-auto::-webkit-scrollbar-track {
  background: var(--border-color);
  border-radius: 10px;
}

.overflow-auto::-webkit-scrollbar-thumb {
  background: var(--primary-600);
  border-radius: 10px;
}

.overflow-auto::-webkit-scrollbar-thumb:hover {
  background: var(--primary-100);
}
      `}</style>

      <Breadcrumb
        title={
          activeDashboard === "admin" ? "Admin Dashboard" : "Client Dashboard"
        }
      />

      <div className="container-fluid p-4">
        <div className="mb-4">
          <div className="btn-group">
            <button
              className={`btn btn-primary-600 ${
                activeDashboard === "admin" ? "active" : ""
              }`}
              onClick={() => setActiveDashboard("admin")}
            >
              Admin Dashboard
            </button>
            <button
              className={`btn btn-primary-600 ${
                activeDashboard === "client" ? "active" : ""
              }`}
              onClick={() => setActiveDashboard("client")}
            >
              Client Dashboard
            </button>
          </div>
        </div>

        {activeDashboard === "admin"
          ? renderAdminDashboard()
          : renderClientDashboard()}

        
      </div>
    </>
  );
};

export default Dashboard;